import * as React from 'react';
import { getElementOverflowPosition } from '@cotera/client/app/components/ui/tiptap/utils';
import {
  SuggestionMenu,
  filterSuggestionItems,
  type SuggestionItem,
  type SuggestionMenuRenderProps,
} from '@cotera/client/app/components/ui/tiptap/tiptap-ui-utils/suggestion-menu';
import { Icon } from '@cotera/client/app/components/ui/icon/icon';
import type { IconName } from '@cotera/client/app/components/ui/icon/mapping';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Card, CardContent } from '@cotera/client/v0/components/ui/card';
import { cn } from '@cotera/client/v0/lib/utils';
import {
  type Action,
  useActionsContext,
} from '@cotera/client/v0/actions/framework';

export const CHAT_ACTION_SLASH_PLUGIN_KEY = 'chatActionSlashDropdownMenu';

type ChatSlashCommand = {
  label: string;
  description?: string;
  icon?: string;
  group?: string;
  keywords?: string[];
  execute: () => Promise<void>;
};

export const isValidSlashCommandInputText = (inputText: string): boolean => {
  return inputText.startsWith('/');
};

const canExecuteWithoutPayload = (action: Action): boolean => {
  return action.inputSchema.safeParse({}).success;
};

const getEditorInputText = (editor: {
  state: {
    doc: {
      textBetween: (
        from: number,
        to: number,
        blockSeparator?: string | null,
        leafText?: string | null
      ) => string;
      content: { size: number };
    };
  };
}): string => {
  return editor.state.doc.textBetween(
    0,
    editor.state.doc.content.size,
    '\n',
    '\0'
  );
};

export const ChatActionSlashDropdownMenu = () => {
  const actionsContext = useActionsContext();

  const getSuggestionItems =
    React.useCallback((): SuggestionItem<ChatSlashCommand>[] => {
      return actionsContext
        .getAllActions()
        .filter((action) => {
          if (action.discrete === true) {
            return false;
          }
          if (!action.applicable(actionsContext)) {
            return false;
          }
          return canExecuteWithoutPayload(action);
        })
        .map((action) => {
          const description = action.description?.trim();
          const command: ChatSlashCommand = {
            label: action.title,
            description: action.description,
            icon: action.icon,
            group: action.group,
            keywords:
              description !== undefined && description !== ''
                ? [description]
                : [],
            execute: async () => {
              if (!action.applicable(actionsContext)) {
                return;
              }
              await action.askOrExecute({}, actionsContext);
            },
          };

          return {
            title: command.label,
            subtext: command.description ?? '',
            group: command.group,
            keywords: command.keywords,
            context: command,
            onSelect: ({ context }) => {
              if (context === undefined) {
                return;
              }
              void context.execute();
            },
          };
        });
    }, [actionsContext]);

  return (
    <SuggestionMenu
      char="/"
      allowSpaces={true}
      pluginKey={CHAT_ACTION_SLASH_PLUGIN_KEY}
      decorationClass="tiptap-slash-decoration"
      decorationContent="Run action..."
      selector="chat-action-slash-dropdown-menu"
      items={({ query, editor }) => {
        const inputText = getEditorInputText(editor);
        if (!isValidSlashCommandInputText(inputText)) {
          return [];
        }
        return filterSuggestionItems(getSuggestionItems(), query);
      }}
      allow={({ state }) => {
        const text = state.doc.textBetween(
          0,
          state.doc.content.size,
          '\n',
          '\0'
        );
        return isValidSlashCommandInputText(text);
      }}
    >
      {(props) => <List {...props} />}
    </SuggestionMenu>
  );
};

const GroupHeader = ({
  label,
  isFirst,
}: {
  label: string;
  isFirst: boolean;
}) => (
  <div
    className={cn(
      'px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider',
      !isFirst && 'mt-2 border-t border-border pt-2'
    )}
  >
    {label}
  </div>
);

const Item = (props: {
  item: SuggestionItem<ChatSlashCommand>;
  isSelected: boolean;
  onSelect: () => void;
}) => {
  const { item, isSelected, onSelect } = props;
  const itemRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    const selector = document.querySelector(
      '[data-selector="chat-action-slash-dropdown-menu"]'
    ) as HTMLElement | null;
    if (itemRef.current === null || isSelected === false || selector === null) {
      return;
    }

    const overflow = getElementOverflowPosition(itemRef.current, selector);

    if (overflow === 'top') {
      itemRef.current.scrollIntoView(true);
    } else if (overflow === 'bottom') {
      itemRef.current.scrollIntoView(false);
    }
  }, [isSelected]);

  const icon = item.context?.icon;

  return (
    <Button
      ref={itemRef}
      variant="ghost"
      className="tiptap-button !justify-start !bg-transparent hover:!bg-gray-100 data-[active-state=on]:!bg-muted data-[active-state=on]:!text-foreground"
      data-active-state={isSelected ? 'on' : 'off'}
      onClick={onSelect}
    >
      <div className="flex items-center gap-2">
        {icon !== undefined ? (
          <Icon icon={icon as IconName} className="w-4 h-4 flex-shrink-0" />
        ) : (
          <div className="w-2 h-2 rounded-full flex-shrink-0 bg-primary" />
        )}
        <span className="tiptap-button-text text-left">{item.title}</span>
      </div>
    </Button>
  );
};

const List = ({
  items,
  selectedIndex,
  onSelect,
}: SuggestionMenuRenderProps<ChatSlashCommand>) => {
  const renderedItems = React.useMemo(() => {
    const rendered: React.ReactElement[] = [];
    const groups: {
      [groupLabel: string]: {
        items: SuggestionItem<ChatSlashCommand>[];
        indices: number[];
      };
    } = {};

    items.forEach((item, index) => {
      const groupLabel = item.group ?? '';
      if (groups[groupLabel] === undefined) {
        groups[groupLabel] = { items: [], indices: [] };
      }
      groups[groupLabel]!.items.push(item);
      groups[groupLabel]!.indices.push(index);
    });

    let isFirstGroup = true;
    Object.entries(groups).forEach(([groupLabel, groupData]) => {
      const groupItems = groupData.items.map((item, itemIndex) => {
        const originalIndex = groupData.indices[itemIndex]!;
        return (
          <Item
            key={`item-${originalIndex}-${item.title}`}
            item={item}
            isSelected={originalIndex === selectedIndex}
            onSelect={() => onSelect(item)}
          />
        );
      });

      if (groupLabel !== '') {
        rendered.push(
          <GroupHeader
            key={`group-header-${groupLabel}`}
            label={groupLabel}
            isFirst={isFirstGroup}
          />,
          ...groupItems
        );
      } else {
        rendered.push(...groupItems);
      }
      isFirstGroup = false;
    });

    return rendered;
  }, [items, selectedIndex, onSelect]);

  if (renderedItems.length === 0) {
    return (
      <Card className="!py-2">
        <CardContent className="w-[200px] !px-2 py-3 text-xs text-muted-foreground">
          No applicable actions
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="!py-2">
      <CardContent className="w-[200px] !px-2 flex flex-col gap-0.5 overflow-y-scroll max-h-[350px]">
        {renderedItems}
      </CardContent>
    </Card>
  );
};
